import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';

import { Grid } from '@material-ui/core';

import { FetchError } from '../../../common/api/api';
import AppSnackbar from '../../../common/AppSnackbar';
import TaskAttachmentCard from '../../tasks/cards/TaskAttachmentCard';
import { Task } from '../../tasks/Task.interface';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import DesignInputDesignOutputReferenceCard from '../cards/DesignInputDesignOutputReferenceCard';
import DesignInputInfoCard from '../cards/DesignInputInfoCard';
import DesignInputUserNeedReferenceCard from '../cards/DesignInputUserNeedReferenceCard';
import {
  useFindProduct,
  useFindProductDesignInputs,
  useProductFilterQueryParams
} from '../Product.hooks';
import {
  BaseProductDesignInputCardProps,
  ProductDesignInput,
  ProductEntity
} from '../Product.interface';
import ProductDocumentCommonPage from './ProductDocumentCommonPage';
import { getProductEntityName } from '../Product.config';

const ProductDesignInputDocumentPage: React.FC = () => {
  const { productId: paramProductId = '' } = useParams<{
    productId?: string;
  }>();
  const productId: number = parseInt(paramProductId) || 0;
  const [didUpdate, setDidUpdate] = useState<boolean>(false);
  const [error, setError] = useState<FetchError | undefined>();
  const [search = '', setSearch] = useQueryParam('search', StringParam);
  const [productDesignInputs, setProductDesignInputs] = useState<
    ProductDesignInput[]
  >([]);
  const {
    productDesignInputList,
    loadingProductDesignInputsError,
    findProductDesignInputs
  } = useFindProductDesignInputs(productId);
  const { product } = useFindProduct(productId, true);
  const { getParams } = useProductFilterQueryParams();

  const createCardProps = (
    productEntity: ProductEntity
  ): BaseProductDesignInputCardProps => {
    const designInput: ProductDesignInput = productEntity as ProductDesignInput;
    return {
      productId,
      productDesignInput: designInput,
      onUpdate: () => setDidUpdate(true),
      onError: (error: FetchError) => setError(error),
      onChange: (newProductDesignInput: Partial<ProductDesignInput>) => {
        const selectedIndex: number = productDesignInputs.findIndex(
          (un: ProductDesignInput) => un.id === designInput.id
        );
        if (selectedIndex > -1) {
          const designInputs: ProductDesignInput[] = [...productDesignInputs];
          designInputs[selectedIndex] = {
            ...designInput,
            ...newProductDesignInput
          };
          setProductDesignInputs(designInputs);
        }
      }
    };
  };

  useEffect(() => {
    findProductDesignInputs({ search, ...getParams() });
  }, [findProductDesignInputs, search, getParams]);

  useEffect(() => {
    if (productDesignInputList) {
      setProductDesignInputs(productDesignInputList.data);
    }
  }, [productDesignInputList]);

  useEffect(() => {
    setError(loadingProductDesignInputsError);
  }, [loadingProductDesignInputsError]);

  return (
    <>
      <ProductDocumentCommonPage
        product={product}
        productEntities={productDesignInputs}
        entity={ProductBoardColumnEntity.DesignInput}
        renderInfoCard={(
          productEntity: ProductEntity,
          title: string,
          hidden?: boolean
        ) => (
          <DesignInputInfoCard
            {...createCardProps(productEntity)}
            title={title}
            hidden={hidden}
            enableTitleRedirect
          />
        )}
        renderGridCards={(productEntity: ProductEntity) => {
          const props: BaseProductDesignInputCardProps = createCardProps(
            productEntity
          );
          return (
            <>
              <Grid item xs={12}>
                <DesignInputUserNeedReferenceCard {...props} />
              </Grid>
              <Grid item xs={12}>
                <DesignInputDesignOutputReferenceCard {...props} />
              </Grid>
              {productEntity.task && (
                <Grid item xs={12}>
                  <TaskAttachmentCard
                    compact
                    task={productEntity.task}
                    onError={props.onError}
                    onChange={(newTask: Partial<Task>) => {
                      Object.assign(productEntity.task, newTask);
                      // update with new task
                      props.onChange?.({});
                      props.onUpdate?.();
                    }}
                  />
                </Grid>
              )}
            </>
          );
        }}
      />

      <AppSnackbar
        severity='error'
        open={!!error}
        onClose={() => setError(undefined)}
      >
        {error?.message}
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={didUpdate}
        onClose={() => didUpdate && setDidUpdate(false)}
      >
        Product {getProductEntityName('designInput')} details updated.
      </AppSnackbar>
    </>
  );
};

export default ProductDesignInputDocumentPage;
